import {
  Address,
  encodeAbiParameters,
  EncodeAbiParametersReturnType,
  parseAbiParameters,
  isAddressEqual,
} from 'viem'
import { base, baseSepolia } from 'viem/chains'

import { formatPercentage, shortenInteger } from '../numbers/formatters'
import { DEFAULT_POOL_CONFIG } from './constants'
import { UniswapV4PoolManager } from '@ourzora/contracts'
import { poolConfigEncodingABI } from '@zoralabs/protocol-deployments'

/**
 * The number of decimals for coin balances
 */
export const COIN_UNITS = 18

/**
 * Converts raw balance (in wei) to coin balance by dividing by 10^18
 * @param rawBalance - The raw balance in wei (smallest unit)
 * @returns The coin balance as a bigint
 */
export function getCoinBalance(rawBalance: bigint): bigint {
  return rawBalance / BigInt(10 ** COIN_UNITS)
}

/**
 * Formats coin percentage as a string, showing '<0.001%' for very small percentages
 * @param balance - The balance to calculate percentage for
 * @param totalSupply - The total supply of coins
 * @returns Formatted percentage string with special handling for small values
 */
export function getFormattedCoinPercentage(balance: string, totalSupply: string): string {
  const _balance = Number(getCoinBalance(BigInt(balance)))
  const _percentage = formatPercentage(_balance / parseFloat(totalSupply), 3)
  const percentage = _percentage === '0%' ? '<0.001%' : _percentage
  return percentage ?? shortenInteger(_balance.toString())
}

/**
 * Encodes the `bytes poolConfig` parameter for coins created post-zero market cap
 */
export function encodePoolConfig(
  version: number,
  currency: Address,
  tickLowers: number[],
  tickUppers: number[],
  numDiscoveryPositions: number[],
  maxDiscoverySupplyShares: bigint[]
): EncodeAbiParametersReturnType {
  const abiParams = parseAbiParameters(
    'uint8, address, int24[], int24[], uint16[], uint256[]'
  )
  return encodeAbiParameters(abiParams, [
    version,
    currency,
    tickLowers,
    tickUppers,
    numDiscoveryPositions,
    maxDiscoverySupplyShares,
  ])
}

interface CustomPoolConfigParams {
  version?: number
  address?: Address
  lowerTicks?: number[]
  upperTicks?: number[]
  numDiscoveryPositions?: number[]
  maxDiscoverySupplyShares?: bigint[]
}

export function buildCustomPoolConfig({
  version = DEFAULT_POOL_CONFIG.VERSION,
  address = DEFAULT_POOL_CONFIG.PAIR_TOKEN_ADDRESS,
  lowerTicks = DEFAULT_POOL_CONFIG.LOWER_TICKS,
  upperTicks = DEFAULT_POOL_CONFIG.UPPER_TICKS,
  numDiscoveryPositions = DEFAULT_POOL_CONFIG.NUM_DISCOVERY_POSITIONS,
  maxDiscoverySupplyShares = DEFAULT_POOL_CONFIG.MAX_DISCOVERY_SUPPLY_SHARES,
}: CustomPoolConfigParams = {}) {
  return encodeAbiParameters(poolConfigEncodingABI[0].inputs, [
    version,
    address,
    lowerTicks,
    upperTicks,
    numDiscoveryPositions,
    maxDiscoverySupplyShares,
  ])
}

export const addressIsMarket = ({
  address,
  uniswapV3PoolAddress,
  chainId,
}: {
  address?: Address
  uniswapV3PoolAddress?: Address
  chainId?: number
}): boolean => {
  const v4PoolManagerAddress =
    chainId && (chainId === base.id || chainId === baseSepolia.id)
      ? UniswapV4PoolManager.address[chainId]
      : null

  return (
    !!address &&
    // Address is the market if the wallet address === the coin's uniswap pool address (for v3).
    ((!!uniswapV3PoolAddress && isAddressEqual(address, uniswapV3PoolAddress)) ||
      // Address is the market if the wallet address === the pool manager address (for v4).
      (!!v4PoolManagerAddress && isAddressEqual(address, v4PoolManagerAddress)))
  )
}
